import type { Node } from '../../core/models/scene';
import { isValidShapeGeometryValue } from './shapeGeometry';

export function valueChanged(previous: unknown, next: unknown): boolean {
  return !Object.is(previous, next);
}

function resolveChangedDimension(
  previous: Node,
  next: Node,
  canonical: 'width' | 'height',
  alias: 'w' | 'h',
): unknown {
  const canonicalChanged = valueChanged(previous[canonical], next[canonical]);
  const aliasChanged = valueChanged(previous[alias], next[alias]);
  if (canonicalChanged && !aliasChanged) return next[canonical];
  if (aliasChanged && !canonicalChanged) return next[alias];
  return next[canonical] ?? next[alias];
}

export function buildFiniteGeometryPatch(previous: Node, next: Node): Record<string, number> {
  const patch: Record<string, number> = {};
  const positionPairs = [
    ['x', previous.x, next.x],
    ['y', previous.y, next.y],
  ] as const;

  for (const [key, previousValue, nextValue] of positionPairs) {
    if (!valueChanged(previousValue, nextValue)) continue;
    if (isValidShapeGeometryValue(key, nextValue)) patch[key] = nextValue;
  }

  const dimensionPairs = [
    ['width', 'w', previous.width ?? previous.w, resolveChangedDimension(previous, next, 'width', 'w')],
    ['height', 'h', previous.height ?? previous.h, resolveChangedDimension(previous, next, 'height', 'h')],
  ] as const;
  for (const [canonical, alias, previousValue, nextValue] of dimensionPairs) {
    if (!valueChanged(previousValue, nextValue)) continue;
    if (!isValidShapeGeometryValue(canonical, nextValue)) continue;
    patch[canonical] = nextValue;
    patch[alias] = nextValue;
  }
  return patch;
}

export function hasNodePropertyChanges(previous: Node, next: Node): boolean {
  return [
    [previous.name, next.name],
    [previous.sex, next.sex],
    [previous.text, next.text],
    [previous.fontSize, next.fontSize],
    [previous.fontFamily, next.fontFamily],
    [(previous as any).deceased, (next as any).deceased],
    [(previous as any).locked, (next as any).locked],
    [(previous as any).zIndex, (next as any).zIndex],
    [(previous as any).location, (next as any).location],
    [(previous as any).strokeWidth, (next as any).strokeWidth],
    [previous.fillColor, next.fillColor],
    [previous.strokeColor, next.strokeColor],
  ].some(([previousValue, nextValue]) => valueChanged(previousValue, nextValue))
    || JSON.stringify(previous.attrs) !== JSON.stringify(next.attrs);
}
